import type { AuthProvider, TokenPair } from "../types";
import { sessionCore } from "../core/session-core";
import { parseJwtUser } from "../core/jwt-decode";
import { refreshWithDedup } from "../core/refresh";
import { authActions } from "../store/auth-store";
import { TOKEN_REFRESH_THRESHOLD_MS } from "../constants";

const LOGIN_TYPE_KEY = "edw.last_login_type";

export interface ColdStartDeps {
  getTokens: () => TokenPair | null;
  canRefresh: () => boolean;
  clear: () => void;
  refresh: () => Promise<TokenPair | null>;
  startRestore: () => void;
  setAuthenticated: (user: any, tokens: TokenPair) => void;
  updateTokens: (tokens: TokenPair) => void;
  reset: () => void;
}

const defaultDeps: ColdStartDeps = {
  getTokens: () => sessionCore.getTokens(),
  canRefresh: () => sessionCore.canRefresh(),
  clear: () => sessionCore.clear(),
  refresh: () => refreshWithDedup(),
  startRestore: () => authActions.startRestore(),
  setAuthenticated: (user, tokens) =>
    authActions.setAuthenticated(user, tokens),
  updateTokens: (tokens) => authActions.updateTokens(tokens),
  reset: () => authActions.reset(),
};

/**
 * 冷启动恢复 — 从 localStorage 恢复认证状态
 *
 * 核心原则：
 * - 有效 JWT → 立即 authenticated（0 帧延迟）
 * - 即将过期 → 立即 authenticated（旧 token），后台刷新
 * - 过期但可刷新 → restoring → 后台刷新
 * - 无 token / 解析失败 → idle
 */
export function coldStartRestore(deps: ColdStartDeps = defaultDeps): void {
  if (typeof window === "undefined") {
    return;
  }

  const tokens = deps.getTokens();
  if (!tokens?.accessToken) {
    return;
  }

  const loginType = detectLoginType();
  let user;
  try {
    user = parseJwtUser(tokens.accessToken, loginType);
  } catch {
    if (!deps.canRefresh()) {
      deps.clear();
      return;
    }
    deps.startRestore();
    deps.refresh().then((refreshed) => {
      if (refreshed) {
        try {
          const freshUser = parseJwtUser(refreshed.accessToken, loginType);
          deps.setAuthenticated(freshUser, refreshed);
        } catch {
          deps.clear();
          deps.reset();
        }
      } else {
        deps.clear();
        deps.reset();
      }
    });
    return;
  }

  const now = Date.now();
  const isExpired = tokens.expiresAt <= now;
  const isExpiringSoon = tokens.expiresAt - now <= TOKEN_REFRESH_THRESHOLD_MS;

  if (!isExpired && !isExpiringSoon) {
    deps.setAuthenticated(user, tokens);
    return;
  }

  if (!isExpired && isExpiringSoon) {
    deps.setAuthenticated(user, tokens);
    if (deps.canRefresh()) {
      deps.refresh().then((refreshed) => {
        if (refreshed) {
          deps.updateTokens(refreshed);
        }
      });
    }
    return;
  }

  if (isExpired && deps.canRefresh()) {
    deps.startRestore();
    deps.refresh().then((refreshed) => {
      if (refreshed) {
        try {
          const freshUser = parseJwtUser(refreshed.accessToken, loginType);
          deps.setAuthenticated(freshUser, refreshed);
        } catch {
          deps.clear();
          deps.reset();
        }
      } else {
        deps.clear();
        deps.reset();
      }
    });
    return;
  }

  deps.clear();
}

const LEGACY_LOGIN_TYPE_MAP: Record<string, AuthProvider> = {
  "1": "google",
  "2": "apple",
  "3": "twitter",
  "5": "email",
};

export function detectLoginType(): AuthProvider {
  try {
    const cached = localStorage.getItem(LOGIN_TYPE_KEY);
    if (
      cached === "google" ||
      cached === "apple" ||
      cached === "twitter" ||
      cached === "email"
    ) {
      return cached;
    }
    const legacyRaw = localStorage.getItem("OSP_LOGIN_CACHE_V1");
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      const mapped = LEGACY_LOGIN_TYPE_MAP[String(legacy.loginType)];
      if (mapped) {
        return mapped;
      }
    }
  } catch {
    /* localStorage unavailable or parse error */
  }
  return "google";
}

export function persistLoginType(provider: AuthProvider): void {
  try {
    localStorage.setItem(LOGIN_TYPE_KEY, provider);
  } catch {
    /* ignore */
  }
}
